
import React, { useState, useMemo, useCallback } from 'react';
import { 
  Receipt, X, ThumbsUp, 
  Camera, 
  PackagePlus, Check, 
  ShieldAlert, 
  FileText, CheckCircle2, HandCoins,
  LayoutGrid, List
} from 'lucide-react';
import { Order, OrderStatus, ExpenseStatus, ExtraItemStatus, DriverPayment, DriverPaymentType } from './types';
import { Card, Badge } from './SharedComponents';

export const EventFinancePage = ({ orders, onUpdateOrders, invoices = [], onUpdateInvoices, onAddDriverPayment, playNotificationSound }: any) => {
  const [viewMode, setViewMode] = useState<'GRID' | 'LIST'>('GRID');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // 1. Faturas do Comercial aguardando Auditoria
  const pendingInvoices = useMemo(() => {
    return (invoices || []).filter((i: any) => i.status === 'AGUARDANDO_FINANCEIRO');
  }, [invoices]);

  // 2. O.S. Novas aguardando Liberação Logística
  const pendingOrders = useMemo(() => {
    return (orders || []).filter((o: Order) => o.status === OrderStatus.AGUARDANDO_FINANCEIRO);
  }, [orders]);

  // 3. CENTRAL DE PENDÊNCIAS DE CAMPO (EXTRA E REEMBOLSOS)
  const pendingAdjustments = useMemo(() => {
    const adjustments: any[] = [];
    if (!orders || !Array.isArray(orders)) return adjustments;

    orders.forEach((o: Order) => {
      // Itens Extras
      if (o.extraItems && Array.isArray(o.extraItems)) {
        o.extraItems.forEach(ex => {
          if (ex.status === ExtraItemStatus.AGUARDANDO_CONTROLADORIA || ex.status === ExtraItemStatus.EXCLUSAO_PENDENTE) {
            adjustments.push({ 
              ...ex, 
              orderName: o.nomeEvento, 
              orderId: o.id, 
              type: 'EXTRA',
              isExclusion: ex.status === ExtraItemStatus.EXCLUSAO_PENDENTE
            });
          }
        });
      }
      // Reembolsos
      if (o.expenses && Array.isArray(o.expenses)) {
        o.expenses.forEach(exp => {
          if (exp.status === ExpenseStatus.PENDENTE_APROVACAO || exp.status === ExpenseStatus.EXCLUSAO_PENDENTE) {
            adjustments.push({ 
              ...exp, 
              orderName: o.nomeEvento, 
              orderId: o.id, 
              type: 'EXPENSE',
              isExclusion: exp.status === ExpenseStatus.EXCLUSAO_PENDENTE
            });
          }
        });
      }
    });
    // Ordenar por data mais recente
    return adjustments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [orders]);

  const handleApproveOS = (orderId: string) => {
    onUpdateOrders((prev: Order[]) => prev.map((o: Order) => o.id === orderId ? { ...o, status: OrderStatus.CONFIRMADO } : o));
    if (playNotificationSound) playNotificationSound();
  };

  const handleApproveInvoiceForBilling = (invoiceId: string) => {
    onUpdateInvoices((prev: any[]) => prev.map((inv: any) => inv.id === invoiceId ? { ...inv, status: 'A_FATURAR' } : inv));
    if (playNotificationSound) playNotificationSound();
  };

  const handleAuditAdjustment = useCallback((adj: any, approved: boolean) => {
    // Gatilho de som imediato no clique para evitar lag perceptível
    if (playNotificationSound) playNotificationSound();

    // 1. Se for aprovação de entrada (não exclusão), registra no financeiro da unidade
    if (approved && !adj.isExclusion) {
        if (onAddDriverPayment) {
            const amount = adj.type === 'EXTRA' ? (Number(adj.qty) * Number(adj.value)) : Number(adj.amount);
            const paymentRecord: DriverPayment = {
                id: `audit-${Date.now()}`,
                userId: adj.userId || 'u-system', 
                type: adj.type === 'EXTRA' ? DriverPaymentType.ADICIONAL : DriverPaymentType.REEMBOLSO,
                amount: amount,
                date: new Date().toISOString(),
                description: `APROV. CONTROLADORIA: ${adj.description || adj.category} (O.S. ${adj.orderId})`,
                status: 'PAID'
            };
            onAddDriverPayment(paymentRecord);
        }
    }

    // 2. Atualiza o estado da Ordem removendo ou alterando o status do item
    onUpdateOrders((prevOrders: Order[]) => prevOrders.map((o: Order) => {
      if (o.id !== adj.orderId) return o;
      
      if (adj.type === 'EXTRA') {
        const updatedExtras = (adj.isExclusion && approved)
          ? o.extraItems?.filter(i => i.id !== adj.id)
          : o.extraItems?.map(i => i.id === adj.id ? { 
              ...i, 
              status: approved ? ExtraItemStatus.APROVADO : ExtraItemStatus.REJEITADO 
            } : i);
        return { ...o, extraItems: updatedExtras };
      } else {
        const updatedExpenses = (adj.isExclusion && approved)
          ? o.expenses?.filter(i => i.id !== adj.id)
          : o.expenses?.map(i => i.id === adj.id ? { 
              ...i, 
              status: approved ? ExpenseStatus.APROVADO : ExpenseStatus.REJEITADO 
            } : i);
        return { ...o, expenses: updatedExpenses };
      }
    }));
  }, [onAddDriverPayment, onUpdateOrders, playNotificationSound]);

  return (
    <div className="space-y-12 pb-20 p-4 md:p-8 lg:p-10 text-secondary">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-secondary uppercase tracking-tighter italic leading-none">Controladoria Master</h1>
          <p className="text-gray-400 text-sm font-medium italic mt-2">Validação de faturamentos e auditoria financeira de campo.</p>
        </div>
        
        <div className="flex items-center gap-4">
           <Badge className="bg-primary/10 text-primary border-none font-black text-[10px] px-4 py-2 uppercase">Live Sync Ativo</Badge>
           <div className="bg-white p-1.5 rounded-2xl border border-gray-100 flex items-center gap-1 shadow-sm shrink-0">
             <button onClick={() => setViewMode('GRID')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'GRID' ? 'bg-primary text-white shadow-lg' : 'text-gray-400 hover:text-secondary'}`}><LayoutGrid size={14} /> Cards</button>
             <button onClick={() => setViewMode('LIST')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'LIST' ? 'bg-primary text-white shadow-lg' : 'text-gray-400 hover:text-secondary'}`}><List size={14} /> Lista</button>
           </div>
        </div>
      </div>

      {/* SEÇÃO 1: AUDITORIA DE CAMPO (EXTRA E REEMBOLSOS) */}
      <section className="space-y-6">
         <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center gap-3">
               <div className="p-2.5 bg-purple-600 text-white rounded-xl shadow-lg shadow-purple-200"><HandCoins size={20} strokeWidth={2.5} /></div>
               <div>
                  <h3 className="text-base font-black uppercase text-secondary tracking-tight italic">Auditoria de Campo (Apps)</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mt-1">Sincronização de motoristas e equipes</p>
               </div>
            </div>
            <div className="flex gap-2">
               {pendingAdjustments.length > 0 && <Badge className="bg-red-500 text-white border-none px-3 py-1 font-black text-[10px] animate-pulse">NOVO LANÇAMENTO</Badge>}
               <Badge className="bg-purple-50 text-purple-600 border-none px-3 py-1 font-black text-[10px]">{pendingAdjustments.length} PENDÊNCIAS</Badge>
            </div>
         </div>
         
         {pendingAdjustments.length > 0 ? (
           <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {pendingAdjustments.map((adj) => (
                 <Card 
                    key={`audit-card-${adj.type}-${adj.orderId}-${adj.id}`} 
                    className={`p-6 bg-white border-2 flex flex-col justify-between gap-6 shadow-xl rounded-[40px] hover:border-purple-300 group ${adj.isExclusion ? 'border-red-100' : 'border-purple-50'}`}
                 >
                    <div className="flex justify-between items-start">
                       <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform ${adj.type === 'EXTRA' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                             {adj.type === 'EXTRA' ? <PackagePlus size={28} /> : <Receipt size={28} />}
                          </div>
                          <div className="min-w-0">
                             <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">{adj.orderName}</p>
                             <h4 className="text-sm font-black text-secondary uppercase truncate leading-none italic">{adj.description || adj.category}</h4>
                          </div>
                       </div>
                       <Badge className={adj.isExclusion ? 'bg-red-500 text-white border-none text-[8px] font-black' : 'bg-purple-100 text-purple-600 border-none text-[8px] font-black'}>
                          {adj.isExclusion ? 'EXCLUSÃO' : adj.type === 'EXTRA' ? 'ITEM EXTRA' : 'REEMBOLSO'}
                       </Badge>
                    </div>
                    <div className="bg-slate-50/50 p-6 rounded-[32px] border border-slate-100 flex items-center justify-between shadow-inner">
                       <div className="space-y-0.5">
                          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{adj.type === 'EXTRA' ? 'Receita Adicional' : 'Custo de Operação'}</p>
                          <p className={`text-2xl font-black tracking-tighter ${adj.type === 'EXTRA' ? 'text-emerald-600' : 'text-red-600'}`}>{adj.type === 'EXTRA' ? '+' : '-'} R$ {(adj.amount || (adj.qty * adj.value)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                       </div>
                       <div className="text-right flex flex-col items-end gap-2">
                         {adj.photo && (<button onClick={() => setSelectedPhoto(adj.photo)} className="bg-purple-600 text-white p-2.5 rounded-xl shadow-lg flex items-center gap-2 active:scale-95 transition-all group-hover:rotate-2"><Camera size={16} /><span className="text-[8px] font-black uppercase">Ver Comprovante</span></button>)}
                         <p className="text-[9px] font-black text-slate-300 uppercase italic tracking-widest">Enviado por: {adj.requestedBy?.toUpperCase() || 'SISTEMA'}</p>
                       </div>
                    </div>
                    <div className="flex gap-2">
                       <button onClick={() => handleAuditAdjustment(adj, false)} className="flex-1 py-4 bg-white text-gray-400 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-red-50 hover:text-red-500 transition-all flex items-center justify-center gap-2 border border-slate-100 active:scale-95 shadow-sm"><X size={16} /> Negar</button>
                       <button onClick={() => handleAuditAdjustment(adj, true)} className="flex-1 py-4 bg-purple-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:brightness-110 shadow-xl shadow-purple-200 transition-all flex items-center justify-center gap-2 active:scale-95"><ThumbsUp size={16} strokeWidth={3} /> Aprovar</button>
                    </div>
                 </Card>
              ))}
           </div>
         ) : (
           <div className="py-24 bg-purple-50/20 rounded-[48px] border-4 border-dashed border-purple-100 flex flex-col items-center justify-center text-center gap-6">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg text-purple-200"><CheckCircle2 size={40} /></div>
              <div><p className="text-xl font-black text-purple-300 uppercase tracking-[0.4em]">Auditoria Sincronizada</p><p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest mt-2">Nenhum lançamento pendente vindo do campo agora.</p></div>
           </div>
         )}
      </section>

      {/* SEÇÃO 2 & 3: FATURAMENTOS E O.S. */}
      <div className={`grid grid-cols-1 gap-10 lg:grid-cols-2`}>
          <section className="space-y-6">
            <h3 className="text-base font-black uppercase text-secondary flex items-center gap-3 italic"><FileText size={20} className="text-blue-500" /> Faturamentos Pendentes</h3>
            <div className="grid grid-cols-1 gap-3">
               {pendingInvoices.map((inv: any) => (
                 <Card key={`inv-${inv.id}`} className="p-6 bg-white border-2 border-gray-50 rounded-[32px] flex items-center justify-between group hover:border-primary/20 transition-all shadow-md">
                    <div>
                       <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{inv.number}</p>
                       <h4 className="text-sm font-black text-secondary uppercase mt-1">{inv.clientName}</h4>
                       <p className="text-lg font-black text-primary mt-2">R$ {inv.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <button onClick={() => handleApproveInvoiceForBilling(inv.id)} className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm active:scale-90"><Check size={20} strokeWidth={3} /></button>
                 </Card>
               ))}
               {pendingInvoices.length === 0 && <p className="text-center py-12 text-[10px] text-gray-300 font-bold uppercase italic border-2 border-dashed border-gray-100 rounded-[32px]">Sem faturas em espera</p>}
            </div>
          </section>

          <section className="space-y-6">
            <h3 className="text-base font-black uppercase text-secondary flex items-center gap-3 italic"><ShieldAlert size={20} className="text-amber-500" /> Liberação de O.S.</h3>
            <div className="grid grid-cols-1 gap-3">
               {pendingOrders.map((o: Order) => (
                 <Card key={`order-${o.id}`} className="p-6 bg-white border-2 border-gray-50 rounded-[32px] flex items-center justify-between group hover:border-amber-200 transition-all shadow-md">
                    <div>
                       <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">ID: {o.id}</p>
                       <h4 className="text-sm font-black text-secondary uppercase mt-1">{o.nomeEvento}</h4>
                       <Badge className="bg-amber-50 text-amber-600 border-none text-[8px] mt-2 font-black">CUSTO EST: R$ {o.estimatedCost.toLocaleString('pt-BR')}</Badge>
                    </div>
                    <button onClick={() => handleApproveOS(o.id)} className="px-6 py-4 bg-amber-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:brightness-110 transition-all active:scale-95">Liberar Saída</button>
                 </Card>
               ))}
               {pendingOrders.length === 0 && <p className="text-center py-12 text-[10px] text-gray-300 font-bold uppercase italic border-2 border-dashed border-gray-100 rounded-[32px]">Sem O.S. para liberação</p>}
            </div>
          </section>
      </div>

      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/95 z-[5000] flex flex-col" onClick={() => setSelectedPhoto(null)}>
           <div className="p-6 flex justify-between items-center bg-black/40 text-white shrink-0">
              <span className="text-sm font-black uppercase tracking-widest italic">Comprovante de Auditoria</span>
              <button className="p-4 bg-white/10 rounded-2xl active:scale-90 transition-all"><X size={32} /></button>
           </div>
           <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
              <img src={selectedPhoto} className="max-w-full max-h-full object-contain rounded-[40px] shadow-2xl border-4 border-white/5 animate-in zoom-in-95" alt="Recibo" />
           </div>
        </div>
      )}
    </div>
  );
};
